import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminUsers, useUpdateUserRole, useSendMagicLink, type UserWithRole } from '@/hooks/useAdmin';
import { useAllCohorts, useAllCohortMemberships, useSetUserCohorts } from '@/hooks/useCohorts';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Search, Loader2, Shield, User, Crown, KeyRound, Link, CheckCircle, XCircle, Clock, MapPin } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import type { Database } from '@/integrations/supabase/types';
import MemberDetailDrawer from '@/components/admin/MemberDetailDrawer';

type UserRole = Database['public']['Enums']['user_role'];

const ROLE_CONFIG: Record<UserRole, { label: string; icon: typeof Shield; className: string }> = {
  admin: { label: 'Admin', icon: Crown, className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  moderator: { label: 'Moderator', icon: Shield, className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  member: { label: 'Member', icon: User, className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' },
};

export default function AdminUsers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'approved');
  const [selectedMember, setSelectedMember] = useState<UserWithRole | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { data: users, isLoading } = useAdminUsers();
  const { data: cohorts } = useAllCohorts();
  const { data: cohortMemberships } = useAllCohortMemberships();
  const setUserCohorts = useSetUserCohorts();

  // Sync tab state with URL
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };
  const queryClient = useQueryClient();
  const updateRole = useUpdateUserRole();
  const sendMagicLink = useSendMagicLink();
  const { toast } = useToast();

  const [sendingTo, setSendingTo] = useState<Record<string, string>>({});

  const handleRowClick = (user: UserWithRole) => {
    setSelectedMember(user);
    setDrawerOpen(true);
  };

  const handleSendLink = async (userId: string, type: 'magiclink' | 'recovery') => {
    const label = type === 'recovery' ? 'Password reset' : 'Login link';
    setSendingTo((prev) => ({ ...prev, [userId]: type }));
    try {
      await sendMagicLink.mutateAsync({ userId, type });
      toast({
        title: `${label} sent`,
        description: `${label} email has been sent to the member.`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSendingTo((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    }
  };

  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const linkCardRef = useRef<HTMLDivElement>(null);

  // Scroll to link card when generated
  useEffect(() => {
    if (generatedLink && linkCardRef.current) {
      linkCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [generatedLink]);

  const handleCopyLink = async (userId: string) => {
    setSendingTo((prev) => ({ ...prev, [userId]: 'generate_link' }));
    try {
      const result = await sendMagicLink.mutateAsync({ userId, type: 'generate_link' });
      setGeneratedLink(result.link);
      // Try to auto-copy to clipboard
      try {
        await navigator.clipboard.writeText(result.link);
        toast({
          title: 'Reset link copied!',
          description: 'Link has been copied to your clipboard.',
        });
      } catch {
        // Clipboard failed, user can copy from the input field
        toast({
          title: 'Reset link generated',
          description: 'Click Copy above to copy it.',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSendingTo((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    }
  };

  const pendingUsers = users?.filter((u) => u.membership_status === 'pending') || [];
  const pendingCount = pendingUsers.length;
  const appliedUsers = users?.filter((u) => u.membership_status === 'applied') || [];
  const appliedCount = appliedUsers.length;

  const filteredUsers = users?.filter((user) => {
    // Tab filter
    if (activeTab === 'pending' && user.membership_status !== 'pending') return false;
    if (activeTab === 'applications' && user.membership_status !== 'applied') return false;
    if (activeTab === 'approved' && user.membership_status !== 'approved') return false;
    if (activeTab === 'rejected' && user.membership_status !== 'rejected') return false;

    // Search filter
    return (
      user.full_name.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase()) ||
      user.company?.toLowerCase().includes(search.toLowerCase()) ||
      user.role?.toLowerCase().includes(search.toLowerCase())
    );
  });

  const handleStatusChange = async (userId: string, newStatus: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({ membership_status: newStatus })
        .eq('user_id', userId)
        .select('membership_status')
        .single();
      if (error) throw error;
      if (!data) throw new Error('Update failed — no rows affected. Check RLS policies.');
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast({ title: 'Status updated', description: `Membership status changed to ${newStatus}.` });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      await updateRole.mutateAsync({ userId, role: newRole });
      toast({
        title: 'Role updated',
        description: `User role has been updated to ${ROLE_CONFIG[newRole].label}.`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleCohortToggle = async (userId: string, cohortId: string, currentCohortIds: string[]) => {
    const newCohortIds = currentCohortIds.includes(cohortId)
      ? currentCohortIds.filter((id) => id !== cohortId)
      : [...currentCohortIds, cohortId];

    try {
      await setUserCohorts.mutateAsync({ userId, cohortIds: newCohortIds });
    } catch (error: any) {
      toast({
        title: 'Error updating cohorts',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">Members</h1>
        <p className="text-muted-foreground text-sm">
          Manage member accounts and roles
        </p>
      </div>

      {generatedLink && (
        <Card ref={linkCardRef} className="border-primary">
          <CardContent className="py-3 flex items-center gap-3">
            <Input
              readOnly
              value={generatedLink}
              className="flex-1 text-xs font-mono"
              onFocus={(e) => e.target.select()}
            />
            <Button
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(generatedLink);
                toast({ title: 'Copied!' });
              }}
            >
              Copy
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setGeneratedLink(null)}
            >
              Dismiss
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                Members
                {appliedCount > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {appliedCount} application{appliedCount !== 1 ? 's' : ''}
                  </Badge>
                )}
                {pendingCount > 0 && (
                  <Badge className="ml-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                    {pendingCount} pending
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {filteredUsers?.length || 0} {activeTab === 'all' ? 'total' : activeTab === 'approved' ? 'active' : activeTab === 'applications' ? 'applied' : activeTab} members
              </CardDescription>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-4">
            <TabsList>
              <TabsTrigger value="approved">Active</TabsTrigger>
              <TabsTrigger value="applications" className="relative">
                Applications
                {appliedCount > 0 && (
                  <span className="ml-1.5 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center">
                    {appliedCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="pending" className="relative">
                Pending
                {pendingCount > 0 && (
                  <span className="ml-1.5 h-5 w-5 rounded-full bg-yellow-500 text-white text-xs flex items-center justify-center">
                    {pendingCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="rejected">Rejected</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Invited By</TableHead>
                  <TableHead>Cohort</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers?.map((user) => {
                  const currentRole = user.user_roles?.role || 'member';

                  return (
                    <TableRow
                      key={user.id}
                      className="even:bg-muted/50 cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => handleRowClick(user)}
                    >
                      <TableCell className="py-2">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-7 w-7">
                            <SignedAvatarImage src={user.avatar_url || undefined} />
                            <AvatarFallback className="bg-primary/10 text-primary text-xs">
                              {user.full_name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate max-w-[140px]">{user.full_name}</div>
                            <div className="text-xs text-muted-foreground truncate max-w-[140px]">
                              {user.company || user.city_cohort || '-'}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <span className="text-xs text-muted-foreground truncate block max-w-[160px]">
                          {user.email || '-'}
                        </span>
                      </TableCell>
                      <TableCell className="py-2">
                        <span className="text-xs text-muted-foreground truncate block max-w-[100px]">
                          {user.invited_by || '-'}
                        </span>
                      </TableCell>
                      <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const userCohorts = cohortMemberships?.get(user.user_id) || [];
                          const userCohortIds = userCohorts.map((c) => c.cohort_id);
                          return (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="flex flex-wrap gap-1 min-w-[80px] max-w-[160px] items-center text-left">
                                  {userCohorts.length > 0 ? (
                                    userCohorts.map((c) => (
                                      <span
                                        key={c.cohort_id}
                                        className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs font-medium"
                                      >
                                        {c.cohort_name}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-xs text-muted-foreground">—</span>
                                  )}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-48 p-2" align="start">
                                <div className="space-y-1">
                                  {cohorts?.map((cohort) => (
                                    <label
                                      key={cohort.id}
                                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm"
                                    >
                                      <Checkbox
                                        checked={userCohortIds.includes(cohort.id)}
                                        onCheckedChange={() =>
                                          handleCohortToggle(user.user_id, cohort.id, userCohortIds)
                                        }
                                      />
                                      <MapPin className="h-3 w-3 text-muted-foreground" />
                                      {cohort.name}
                                    </label>
                                  ))}
                                  {(!cohorts || cohorts.length === 0) && (
                                    <p className="text-xs text-muted-foreground px-2 py-1">No cohorts configured</p>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={user.membership_status}
                          onValueChange={(value) =>
                            handleStatusChange(user.user_id, value)
                          }
                        >
                          <SelectTrigger className="w-[100px] h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="applied">
                              <span className="flex items-center gap-1.5">
                                <Clock className="h-3 w-3 text-blue-500" />
                                Applied
                              </span>
                            </SelectItem>
                            <SelectItem value="pending">
                              <span className="flex items-center gap-1.5">
                                <Clock className="h-3 w-3 text-yellow-500" />
                                Pending
                              </span>
                            </SelectItem>
                            <SelectItem value="approved">
                              <span className="flex items-center gap-1.5">
                                <CheckCircle className="h-3 w-3 text-green-500" />
                                Active
                              </span>
                            </SelectItem>
                            <SelectItem value="rejected">
                              <span className="flex items-center gap-1.5">
                                <XCircle className="h-3 w-3 text-red-500" />
                                Rejected
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={currentRole}
                          onValueChange={(value) =>
                            handleRoleChange(user.user_id, value as UserRole)
                          }
                        >
                          <SelectTrigger className="w-[90px] h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="member">Member</SelectItem>
                            <SelectItem value="moderator">Mod</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  disabled={!!sendingTo[user.user_id]}
                                  onClick={() => handleSendLink(user.user_id, 'recovery')}
                                >
                                  {sendingTo[user.user_id] === 'recovery' ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <KeyRound className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Send reset email</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  disabled={!!sendingTo[user.user_id]}
                                  onClick={() => handleCopyLink(user.user_id)}
                                >
                                  {sendingTo[user.user_id] === 'generate_link' ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Link className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Copy reset link</TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
          )}
        </CardContent>
      </Card>

      <MemberDetailDrawer
        member={selectedMember}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
}
